import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ImageValidatorHelperService } from 'src/app/services/image-validator-helper.service';
import { ApiService } from 'src/app/services/api.service';
import { SharedDirectivesModule } from 'src/app/shared/modules/shared-directives.module';
import { SharedComponentsModule } from 'src/app/shared/modules/shared-component.module';
import { MegapayLoaderComponent } from 'src/app/shared/component/megapay-loader/megapay-loader.component';
import { DISBURSEMENT_INSTITUTION_TYPE_OPTIONS } from 'src/app/lib/constants/disbursement.constants';
import * as vo from 'src/app/core/model/server/vo/_index';

export interface CreateEditDisbursementDialogData {
  mode: 'create' | 'edit';
  item?: vo.IDisbursementInstitution; // row from the list (edit mode)
}

const ALLOWED_TYPES = ['image/png', 'image/jpeg'];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_DIMENSION = 500;              // px

@Component({
  selector: 'app-create-edit-disbursement-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    SharedComponentsModule,
    SharedDirectivesModule,
    MegapayLoaderComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './create-edit-disbursement-modal.component.html',
  styleUrl: './create-edit-disbursement-modal.component.scss',
})
export class CreateEditDisbursementModalComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  private readonly REQUIRED_FIELDS = ['code', 'name', 'institutionType'];
  private readonly BORDER_DEFAULT = '#5F4D9C';
  private readonly BORDER_ERROR   = '#e3342f';

  form!: UntypedFormGroup;
  isEditMode = false;
  submitted = false;
  institutionType_text = '';
  institutionTypeOptions = [...DISBURSEMENT_INSTITUTION_TYPE_OPTIONS];

  // ─── Image state ───────────────────────────────────────────────────────────
  selectedFile: File | null = null;   // newly picked (create or replace)
  existingImageUrl: string | null = null; // current image (edit mode)

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreateEditDisbursementModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateEditDisbursementDialogData,
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private imageValidator: ImageValidatorHelperService
  ) {}

  ngOnInit(): void {
    this.isEditMode = this.data?.mode === 'edit';

    this.form = this.fb.group({
      code:                 ['', Validators.required],
      name:                 ['', Validators.required],
      institutionType:      ['', Validators.required],
      serviceFeePercentage: [null, [Validators.min(0), Validators.max(100)]],
      serviceFeeFixed:      [null, Validators.min(0)],
      convenienceFee:       [null, Validators.min(0)],
    });

    if (this.isEditMode && this.data.item) {
      this.patchEditValues(this.data.item);
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────
  get title(): string {
    return this.isEditMode ? 'UPDATE INSTITUTION' : 'ADD INSTITUTION';
  }

  get hasImage(): boolean {
    return !!this.selectedFile || !!this.existingImageUrl;
  }

  get imageLabel(): string {
    const name = this.selectedFile?.name
      ?? this.existingImageUrl?.split('/').pop()
      ?? '';
    return name.replace(/\.[^.]+$/, ''); // hide extension, like the mockup
  }

  get imageInvalid(): boolean {
    return this.submitted && !this.hasImage;
  }
  
  setInstitutionType(text: string, value: any): void {
    this.form.patchValue({ institutionType: value });
    this.institutionType_text = text;
  }

  /** true when the field should show the red border (only after a submit attempt) */
  isInvalid(controlName: string): boolean {
    if (!this.submitted) { return false; }
    const control = this.form.get(controlName);
    if (!control) { return false; }

    if (this.REQUIRED_FIELDS.includes(controlName)) {
      const value = control.value;
      const empty = value === null || value === undefined || String(value).trim() === '';
      return empty || control.invalid;
    }
    return control.invalid; // optional fields: only range errors
  }

  borderColor(controlName: string): string {
    return this.isInvalid(controlName) ? this.BORDER_ERROR : this.BORDER_DEFAULT;
  }

  get imageBorderColor(): string {
    return this.imageInvalid ? this.BORDER_ERROR : this.BORDER_DEFAULT;
  }


  private patchEditValues(item: vo.IDisbursementInstitution): void {
    this.form.patchValue({
      code:                 item.code,
      name:                 item.name,
      institutionType:      item.institutionType,
      serviceFeePercentage: item.serviceFeePercentage,
      serviceFeeFixed:      item.serviceFeeFixed,
      convenienceFee:       item.convenienceFee,
    });
    this.existingImageUrl = item.imageUrl || null;

    // keep the current type selectable even if it isn't in the constant
    const exists = this.institutionTypeOptions.some(o => o.value === item.institutionType);
    if (item.institutionType && !exists) {
      this.institutionTypeOptions = [
        ...this.institutionTypeOptions,
        { label: item.institutionType, value: item.institutionType },
      ];
    }

    // show the current type's label in the dropdown
    this.institutionType_text =
      this.institutionTypeOptions.find(o => o.value === item.institutionType)?.label
      ?? item.institutionType
      ?? '';
  }

  // ─── Image upload / replace ────────────────────────────────────────────────
  openFilePicker(): void {
    this.fileInput.nativeElement.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // allow re-picking the same file
    if (!file) { return; }

    const result = await this.imageValidator.processFileSelection(
      file,
      MAX_FILE_BYTES,
      MAX_DIMENSION,
    );

    if (result) { this.selectedFile = result.file; }
  }

  private hasValidDimensions(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img.width <= MAX_DIMENSION && img.height <= MAX_DIMENSION);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };
      img.src = url;
    });
  }

  // ─── Validation ────────────────────────────────────────────────────────────
  private isFormValid(): boolean {
    this.submitted = true;
    this.form.markAllAsTouched();

    const missing: string[] = [];
    const invalid: string[] = [];

    if (this.isInvalid('code'))            { missing.push('Please enter Code'); }
    if (this.isInvalid('name'))            { missing.push('Please enter Name'); }
    if (this.isInvalid('institutionType')) { missing.push('Please select Institution Type'); }
    if (!this.hasImage)                    { missing.push('Please upload an image'); }

    if (this.isInvalid('serviceFeePercentage')) { invalid.push('Service Fee Percentage must be between 0 - 100'); }
    if (this.isInvalid('serviceFeeFixed'))      { invalid.push('Service Fee Fixed must not be negative'); }
    if (this.isInvalid('convenienceFee'))       { invalid.push('Convenience Fee must not be negative'); }

    const total = missing.length + invalid.length;
    if (total === 0) { return true; }

    if (total > 1) {
      // several problems: one general toast
      if (invalid.length === 0) {
        this.toastr.error('Please fill in all the required fields', 'Required Field Missing');
      } else {
        this.toastr.error('Please check the highlighted fields', 'Invalid Input');
      }
    } else if (missing.length === 1) {
      // exactly one problem: specific toast
      this.toastr.error(missing[0], 'Required Field Missing');
    } else {
      this.toastr.error(invalid[0], 'Invalid Value');
    }

    return false;
  }

  // ─── Submit ────────────────────────────────────────────────────────────────
  private buildFormData(): FormData {
    const raw = this.form.getRawValue();
    const formData = new FormData();

    if (this.isEditMode) { formData.append('uid', String(this.data.item!.uid)); }

    formData.append('code', raw.code.trim());
    formData.append('name', raw.name.trim());
    formData.append('institutionType', raw.institutionType);

    // optional fees: only send when filled
    (['serviceFeePercentage', 'serviceFeeFixed', 'convenienceFee'] as const).forEach((key) => {
      const value = raw[key];
      if (value !== null && value !== '') { formData.append(key, String(value)); }
    });

    // image: required on create, only sent on edit when replaced
    if (this.selectedFile) { formData.append('image', this.selectedFile); }

    return formData;
  }

  async submit(): Promise<void> {
    if (!this.isFormValid()) { return; }

    this.spinner.show().catch(console.error);

    try {
      const formData = this.buildFormData();
      const response = this.isEditMode
        ? await this.apiService.updateDisbursementInstitution(formData)
        : await this.apiService.createDisbursementInstitution(formData);

      if (response.status === 200) {
        this.toastr.success(
          this.isEditMode ? 'Institution updated successfully.' : 'Institution created successfully.',
          'Success!'
        );
        this.dialogRef.close(true);
      } else {
        this.toastr.error(response.message || 'Request failed.', 'Something went wrong!');
      }
    } catch (error: any) {
      this.toastr.error(error?.error?.message || error?.message || 'Something went wrong!', 'Error!');
    } finally {
      this.spinner.hide().catch(console.error);
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
